/** @format */

import { useEffect, type RefObject } from 'react';

const DESKTOP_MIN_WIDTH_PX = 1160;
const SIDEBAR_STICKY_GAP_PX = 24;

const readHeaderStickyTop = (): number => {
  const root = document.documentElement;
  const headerEl = document.querySelector('header');
  const fullHeight =
    parseFloat(getComputedStyle(root).getPropertyValue('--header-full-height')) ||
    headerEl?.offsetHeight ||
    0;
  const hiddenPx = parseFloat(getComputedStyle(root).getPropertyValue('--header-hidden-px')) || 0;
  const visibleHeight = Math.max(0, fullHeight - hiddenPx);

  return visibleHeight + SIDEBAR_STICKY_GAP_PX;
};

const useTopicsSidebarSticky = (
  sidebarRef: RefObject<HTMLElement | null>,
  contentRef?: RefObject<HTMLElement | null>
): void => {
  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    const contentEl = contentRef?.current;
    if (!sidebarEl) return undefined;

    let rafId = 0;

    const clearVars = () => {
      document.documentElement.style.removeProperty('--topics-sidebar-top');
      document.documentElement.style.removeProperty('--topics-sidebar-height');
      sidebarEl.classList.remove('topics-page__sidebar--pinned');
    };

    const update = () => {
      rafId = 0;

      if (window.innerWidth < DESKTOP_MIN_WIDTH_PX) {
        clearVars();
        return;
      }

      const stickyTop = readHeaderStickyTop();
      const layoutTop = sidebarEl.parentElement?.getBoundingClientRect().top ?? stickyTop;

      const isPinned = layoutTop <= stickyTop;
      const effectiveTop = isPinned ? stickyTop : layoutTop;
      const viewportHeight = Math.max(0, window.innerHeight - effectiveTop);
      const contentHeight = contentEl?.offsetHeight ?? viewportHeight;
      const layoutBottom = sidebarEl.parentElement?.getBoundingClientRect().bottom ?? window.innerHeight;
      const availableInLayout = Math.max(0, layoutBottom - effectiveTop);
      const maxAllowedHeight = Math.max(contentHeight, viewportHeight);
      const sidebarHeight = Math.min(viewportHeight, availableInLayout, maxAllowedHeight);

      if (isPinned) {
        sidebarEl.classList.add('topics-page__sidebar--pinned');
        document.documentElement.style.setProperty('--topics-sidebar-top', `${stickyTop}px`);
      } else {
        sidebarEl.classList.remove('topics-page__sidebar--pinned');
        document.documentElement.style.removeProperty('--topics-sidebar-top');
      }

      document.documentElement.style.setProperty('--topics-sidebar-height', `${sidebarHeight}px`);
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(update);
    };

    const resizeObserver =
      contentEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null;
    if (contentEl) resizeObserver?.observe(contentEl);

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('header-scroll-layout', scheduleUpdate);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('header-scroll-layout', scheduleUpdate);
      if (rafId) window.cancelAnimationFrame(rafId);
      clearVars();
    };
  }, [sidebarRef, contentRef]);
};

export default useTopicsSidebarSticky;
